import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, Save, X, Image as ImageIcon, Link as LinkIcon, Copy, BarChart3 } from 'lucide-react';
import { databaseService, storageService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import RichTextEditor from '../../../components/RichTextEditor/RichTextEditor';

const CERTIFICATES_COLLECTION = 'certificates';
const CERTIFICATE_CATEGORIES_COLLECTION = 'certificate_categories';
const CERTIFICATES_BUCKET = 'certificates';

const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export default function CertificateUpload() {
  const [certificates, setCertificates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    useSlug: true,
    description: '',
    category: '',
    imageUrl: '',
    verifyUrl: '',
    featured: false,
    tags: []
  });
  
  const [tagInput, setTagInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // Show 12 items per page
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [certsResult, categoriesResult] = await Promise.all([
        databaseService.listDocuments(CERTIFICATES_COLLECTION),
        databaseService.listDocuments(CERTIFICATE_CATEGORIES_COLLECTION)
      ]);

      if (certsResult.success) {
        setCertificates(certsResult.data.documents);
      }
      if (categoriesResult.success) {
        setCategories(categoriesResult.data.documents.sort((a, b) => (a.order || 0) - (b.order || 0)));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };


  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileId = `cert_${timestamp}_${randomStr}.${fileExt}`;

      const result = await storageService.uploadFile(CERTIFICATES_BUCKET, fileId, file);
      if (result.success) {
        const fileUrl = storageService.getFileView(CERTIFICATES_BUCKET, result.data.$id);
        setFormData(prev => ({ ...prev, imageUrl: fileUrl }));
        showToast('Image uploaded successfully', 'success');
      } else {
        showToast('Failed to upload image', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('Failed to upload image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.imageUrl || !formData.category) {
      showToast('Title, image, and category are required', 'error');
      return;
    }

    const slug = formData.useSlug ? (formData.slug || generateSlug(formData.title)) : '';

    try {
      const dataToSave = {
        title: formData.title.trim(),
        slug: slug,
        description: formData.description,
        category: formData.category,
        imageUrl: formData.imageUrl,
        verifyUrl: formData.verifyUrl.trim(),
        featured: formData.featured,
        tags: formData.tags || []
      };

      let result;
      if (editingId) {
        result = await databaseService.updateDocument(
          CERTIFICATES_COLLECTION, editingId, dataToSave
        );
      } else {
        result = await databaseService.createDocument(
          CERTIFICATES_COLLECTION, dataToSave
        );
      }

      if (result.success) {
        showToast(editingId ? 'Certificate updated successfully' : 'Certificate added successfully', 'success');
        resetForm();
        loadData();
      } else {
        showToast('Failed to save certificate', 'error');
      }
    } catch (error) {
      console.error('Save error:', error);
      showToast('Failed to save certificate', 'error');
    }
  };

  const handleEdit = (cert) => {
    setEditingId(cert.$id);
    setFormData({
      title: cert.title,
      slug: cert.slug || '',
      useSlug: !!cert.slug,
      description: cert.description || '',
      category: cert.category,
      imageUrl: cert.imageUrl,
      verifyUrl: cert.verifyUrl || '',
      featured: cert.featured || false,
      tags: cert.tags || []
    });
    setShowForm(true);
  };

  const copyCertLink = (slug) => {
    const url = `${window.location.origin}/certificate/${slug}`;
    navigator.clipboard.writeText(url);
    showToast('Link copied to clipboard!', 'success');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;

    try {
      const result = await databaseService.deleteDocument(CERTIFICATES_COLLECTION, id);
      if (result.success) {
        showToast('Certificate deleted successfully', 'success');
        loadData();
      } else {
        showToast('Failed to delete certificate', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Failed to delete certificate', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', slug: '', useSlug: true, description: '', category: '', imageUrl: '', verifyUrl: '', featured: false, tags: [] });
    setTagInput('');
    setEditingId(null);
    setShowForm(false);
  };

  const getCategoryColor = (categoryName) => {
    const cat = categories.find(c => c.name === categoryName);
    return cat?.color || '#4C1D95';
  };

  // Pagination logic
  const totalPages = Math.ceil(certificates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCertificates = certificates.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when filtering/data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [certificates.length]);

  if (loading) {
    return <div className="p-6"><p className="text-gray-500">Loading...</p></div>;
  }


  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#4C1D95' }}>Certificate Upload</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:scale-105"
          style={{ backgroundColor: '#4C1D95' }}
        >
          <Plus className="w-5 h-5" />
          Add Certificate
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-lg font-bold mb-4" style={{ color: '#4C1D95' }}>
            {editingId ? 'Edit Certificate' : 'Add New Certificate'}
          </h3>
          
          <div className="space-y-6">
            {/* Top Row: Image Upload and Basic Fields */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left: Image Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Certificate Image (A4 Landscape)</label>
                <div 
                  className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-[#4C1D95] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ borderColor: formData.imageUrl ? '#4C1D95' : '#d1d5db' }}
                >
                  {formData.imageUrl ? (
                    <img 
                      src={formData.imageUrl} 
                      alt="Preview" 
                      className="max-h-60 mx-auto rounded-lg object-contain"
                    />
                  ) : (
                    <div className="py-12">
                      <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">Click to upload A4 landscape image</p>
                      <p className="text-xs text-gray-400 mt-1">Recommended: 3508 × 2480 px</p>
                    </div>
                  )}
                  {uploading && (
                    <div className="mt-2 flex items-center justify-center gap-2 text-sm text-gray-500">
                      <div className="w-4 h-4 border-2 border-[#4C1D95] border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Right: Basic Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{ borderColor: '#4C1D95' }}
                    placeholder="e.g., Data Analysis with Python"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Custom URL Slug</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-gray-500">{formData.useSlug ? 'Enabled' : 'Disabled'}</span>
                      <div 
                        className={`relative w-10 h-5 rounded-full transition-colors ${formData.useSlug ? 'bg-[#4C1D95]' : 'bg-gray-300'}`}
                        onClick={() => setFormData({ ...formData, useSlug: !formData.useSlug })}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.useSlug ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </div>
                    </label>
                  </div>
                  {formData.useSlug && (
                    <>
                      <div className="flex gap-2">
                        <div className="flex-1 flex items-center border rounded-lg overflow-hidden" style={{ borderColor: '#4C1D95' }}>
                          <span className="px-3 py-2 bg-gray-100 text-gray-600 text-sm border-r" style={{ borderColor: '#4C1D95' }}>
                            /certificate/
                          </span>
                          <input
                            type="text"
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '') })}
                            className="flex-1 px-3 py-2 focus:outline-none"
                            placeholder="your-certificate-slug"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, slug: generateSlug(formData.title) })}
                          className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                          style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
                          title="Auto-generate from title"
                        >
                          Auto
                        </button>
                      </div>
                      {formData.slug && (
                        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                          <LinkIcon className="w-3 h-3" />
                          Preview: {window.location.origin}/certificate/{formData.slug}
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{ borderColor: '#4C1D95' }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.$id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Verify Certificate URL (Optional)</label>
                  <input
                    type="url"
                    value={formData.verifyUrl}
                    onChange={(e) => setFormData({ ...formData, verifyUrl: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{ borderColor: '#4C1D95' }}
                    placeholder="https://verify.example.com/certificate/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Link to verify this certificate (leave empty if not available)</p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 rounded accent-[#4C1D95]"
                  />
                  <label htmlFor="featured" className="text-sm font-medium">
                    Feature on Homepage
                  </label>
                </div>
              </div>
            </div>

            {/* Full Width Description Field */}
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Enter detailed description of the certificate. You can format text with bold, italic, underline, colors, highlights, and choose from 80+ fonts..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Use the toolbar to format your text with bold, italic, underline, colors, highlights, and different fonts. All formatting will be preserved in the certificate popup.
              </p>
            </div>

            {/* Tags Section */}
            <div>
              <label className="block text-sm font-medium mb-2">Tags</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const tag = tagInput.trim();
                      if (tag && !formData.tags.includes(tag)) {
                        setFormData({ ...formData, tags: [...formData.tags, tag] });
                        setTagInput('');
                      }
                    }
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#4C1D95' }}
                  placeholder="Enter tag and press Enter (e.g., Professional, GIS, ESRI)"
                />
                <button
                  type="button"
                  onClick={() => {
                    const tag = tagInput.trim();
                    if (tag && !formData.tags.includes(tag)) {
                      setFormData({ ...formData, tags: [...formData.tags, tag] });
                      setTagInput('');
                    }
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#4C1D95]/10 text-[#4C1D95] text-sm rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          tags: formData.tags.filter((_, i) => i !== index)
                        })}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Add tags to help categorize and filter your certificates
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleSubmit}
                disabled={uploading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Save className="w-4 h-4" />
                {editingId ? 'Update' : 'Save'}
              </button>
              <button
                onClick={resetForm}
                className="flex items-center gap-2 px-6 py-2 rounded-lg border hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Certificates Grid - 3 per row for landscape */}
      {certificates.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Certificates Yet</h3>
          <p className="text-gray-500">Click "Add Certificate" to upload your first certificate.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedCertificates.map(cert => (
            <div 
              key={cert.$id} 
              className="bg-white rounded-lg shadow-md overflow-hidden group relative"
            >
              <div className="aspect-[4/3] relative">
                <img 
                  src={cert.imageUrl} 
                  alt={cert.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                {cert.featured && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">
                    Featured
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleEdit(cert)}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4 text-gray-700" />
                  </button>
                  <Link
                    to={`/admin/analytics/certificates/${cert.$id}`}
                    className="p-2 bg-purple-500 rounded-lg hover:bg-purple-600"
                    title="View Analytics"
                  >
                    <BarChart3 className="w-4 h-4 text-white" />
                  </Link>
                  {cert.slug && (
                    <button
                      onClick={() => copyCertLink(cert.slug)}
                      className="p-2 bg-white rounded-lg hover:bg-gray-100"
                      title="Copy URL"
                    >
                      <Copy className="w-4 h-4 text-gray-700" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(cert.$id)}
                    className="p-2 bg-red-500 rounded-lg hover:bg-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <span 
                  className="text-xs font-bold px-2 py-1 rounded-full text-white"
                  style={{ backgroundColor: getCategoryColor(cert.category) }}
                >
                  {cert.category}
                </span>
                <h4 className="font-semibold mt-2 text-sm line-clamp-1" style={{ color: '#4C1D95' }}>
                  {cert.title}
                </h4>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {certificates.length > itemsPerPage && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    currentPage === pageNum
                      ? 'bg-[#4C1D95] text-white'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
          <span className="ml-4 text-sm text-gray-500">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, certificates.length)} of {certificates.length}
          </span>
        </div>
      )}
    </div>
  );
}

